import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, Typography, Button, Paper, Stack, Chip, IconButton, Tooltip, TextField, Fade, Snackbar, Alert } from '@mui/material';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import CampaignIcon from '@mui/icons-material/Campaign';
import dayjs from 'dayjs';
import AppHeader from '../../components/AppHeader';

const API_URL = process.env.REACT_APP_API_URL1;

const AnnouncementList = ({ setIsLoggedIn }) => {
    const token = useMemo(() => sessionStorage.getItem('token'), []);
    const tenant = useMemo(() => localStorage.getItem('tenant'), []);
    const navigate = useNavigate();
    const config = useMemo(() => ({
        headers: {
            Authorization: `Bearer ${token}`,
            'X-Tenant': tenant
        }
    }), [token, tenant]);

    const [rowData, setRowData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const fetchAnnouncements = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API_URL}/sm/announcements/admin`, config);
            if (res.data.success) setRowData(res.data.data);
        } catch (err) {
            console.error('Failed to fetch announcements:', err);
        } finally {
            setLoading(false);
        }
    }, [config]);

    useEffect(() => {
        fetchAnnouncements();
    }, [fetchAnnouncements]);

    const handleDelete = useCallback(async (id) => {
        if (!window.confirm('Are you sure you want to delete this announcement?')) return;
        try {
            await axios.delete(`${API_URL}/sm/announcements/${id}`, config);
            setSnackbar({ open: true, message: 'Announcement deleted.', severity: 'success' });
            fetchAnnouncements();
        } catch (err) {
            console.error('Delete failed:', err);
            setSnackbar({ open: true, message: 'Failed to delete announcement.', severity: 'error' });
        }
    }, [config, fetchAnnouncements]);

    const columnDefs = useMemo(() => [
        {
            field: 'title',
            headerName: 'Title',
            flex: 1,
            minWidth: 220,
            cellStyle: { fontWeight: 600, color: '#0f172a' }
        },
        {
            field: 'status',
            headerName: 'Status',
            width: 130,
            cellRenderer: (params) => {
                const colorMap = { DRAFT: 'default', SCHEDULED: 'warning', PUBLISHED: 'success', ARCHIVED: 'error' };
                return <Chip size="small" label={params.value} color={colorMap[params.value] || 'default'} />;
            }
        },
        {
            field: 'priority',
            headerName: 'Priority',
            width: 120,
            cellRenderer: (params) => {
                const isHigh = params.value === 'HIGH' || params.value === 'URGENT';
                return <Chip size="small" label={params.value} color={isHigh ? 'error' : 'default'} variant={isHigh ? 'filled' : 'outlined'} />;
            }
        },
        {
            field: 'target_type',
            headerName: 'Target',
            width: 140,
            cellRenderer: (params) => {
                const iconMap = { GLOBAL: '🌐', DEPARTMENT: '🏢', BRANCH: '🏬' };
                return <span>{iconMap[params.value] || ''} {params.value}</span>;
            }
        },
        {
            field: 'publish_date',
            headerName: 'Publish Date',
            width: 150,
            valueFormatter: (p) => p.value ? dayjs(p.value).format('DD MMM YYYY') : '-'
        },
        {
            field: 'read_count',
            headerName: 'Reads / Acks',
            width: 130,
            cellStyle: { textAlign: 'center' },
            cellRenderer: (p) => p.data.require_acknowledgement
                ? <Chip label={`${p.data.ack_count || 0} / ${p.value || 0}`} size="small" color="info" variant="outlined" />
                : <Chip label={p.value || 0} size="small" variant="outlined" />
        },
        {
            headerName: 'Actions',
            width: 120,
            pinned: 'right',
            sortable: false,
            filter: false,
            cellRenderer: (params) => (
                <Stack direction="row" spacing={0.5} alignItems="center" height="100%">
                    <Tooltip title="Edit">
                        <IconButton size="small" color="primary" onClick={() => navigate(`/announcements/edit/${params.data.id}`)}>
                            <EditIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={() => handleDelete(params.data.id)}>
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Stack>
            )
        }
    ], [navigate, handleDelete]);

    const defaultColDef = useMemo(() => ({
        sortable: true,
        filter: true,
        resizable: true,
    }), []);

    return (
        <Box sx={{ minHeight: '100vh', backgroundColor: '#f8fafc', display: 'flex', flexDirection: 'column' }}>
            <AppHeader onLogout={() => navigate('/login')} />

            <Fade in timeout={500}>
                <Box sx={{ p: 4, maxWidth: 1600, mx: 'auto', width: '100%', flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                    {/* Page Header */}
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={4}>
                        <Box>
                            <Stack direction="row" alignItems="center" spacing={1.5}>
                                <CampaignIcon sx={{ color: 'primary.main', fontSize: 36 }} />
                                <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a', letterSpacing: '-0.02em' }}>
                                    Announcements
                                </Typography>
                            </Stack>
                            <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500, mt: 0.5, ml: 6 }}>
                                Manage company-wide and targeted announcements
                            </Typography>
                        </Box>
                        <Stack direction="row" spacing={1.5}>
                            <Tooltip title="Refresh">
                                <IconButton onClick={fetchAnnouncements} sx={{ bgcolor: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
                                    <RefreshIcon />
                                </IconButton>
                            </Tooltip>
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={() => navigate('/announcements/new')}
                                sx={{
                                    bgcolor: 'primary.main',
                                    borderRadius: 2.5,
                                    px: 3, py: 1.2,
                                    fontWeight: 700,
                                    textTransform: 'none',
                                    boxShadow: (theme) => `0 4px 14px 0 ${theme.palette.primary.main}33`,
                                    '&:hover': { bgcolor: 'primary.dark' }
                                }}
                            >
                                New Announcement
                            </Button>
                        </Stack>
                    </Stack>

                    {/* Grid Card */}
                    <Paper sx={{ borderRadius: 4, boxShadow: '0 4px 20px 0 rgba(0,0,0,0.04)', overflow: 'hidden', flex: 1, display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ p: 2.5, borderBottom: '1px solid #f1f5f9', bgcolor: '#fafbfc' }}>
                            <TextField
                                size="small"
                                placeholder="Quick Search..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                sx={{ width: 280, '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fff' } }}
                            />
                        </Box>
                        <Box sx={{ height: 600 }}>
                            <div className="ag-theme-quartz" style={{ height: '100%', width: '100%' }}>
                                <AgGridReact
                                    rowData={rowData}
                                    columnDefs={columnDefs}
                                    defaultColDef={defaultColDef}
                                    quickFilterText={search}
                                    rowHeight={52}
                                    headerHeight={48}
                                    pagination={true}
                                    paginationPageSize={20}
                                    animateRows={true}
                                    loading={loading}
                                    overlayNoRowsTemplate="<span style='color:#64748b'>No announcements found</span>"
                                />
                            </div>
                        </Box>
                    </Paper>
                </Box>
            </Fade>

            <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(s => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert severity={snackbar.severity} variant="filled" onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default AnnouncementList;
